/**
 * Filename validation utilities for knowledgebase compatibility.
 * 
 * Brackets ([ and ]) and slashes (/ and \) are not allowed in filenames 
 * because they cause issues with the knowledgebase indexing, even though 
 * they are valid characters for Windows and S3.
 */

/**
 * Characters that are not allowed in filenames for knowledgebase compatibility.
 */
export const INVALID_FILENAME_CHARS = ['[', ']', '/', '\\'];

/**
 * Validates a filename for knowledgebase compatibility.
 * 
 * @param {string} filename - The filename to validate
 * @returns {{ isValid: boolean, error: string | null }} Validation result
 */
export function validateFilename(filename) {
    if (!filename || typeof filename !== 'string') {
        return { isValid: false, error: 'Filename is required' };
    }

    const trimmedFilename = filename.trim();
    
    if (!trimmedFilename) {
        return { isValid: false, error: 'Filename cannot be empty' };
    }

    // Check for brackets
    if (trimmedFilename.includes('[') || trimmedFilename.includes(']')) {
        return { 
            isValid: false, 
            error: 'Filename cannot contain brackets ([ or ]). These characters are not compatible with the knowledgebase.' 
        };
    }

    // Check for slashes
    if (trimmedFilename.includes('/') || trimmedFilename.includes('\\')) {
        return { 
            isValid: false, 
            error: 'Filename cannot contain slashes (/ or \\). These characters are not compatible with the knowledgebase.' 
        };
    }

    return { isValid: true, error: null };
}

/**
 * Checks if a filename contains invalid characters (brackets or slashes).
 * 
 * @param {string} filename - The filename to check
 * @returns {boolean} True if the filename contains invalid characters
 */
export function hasInvalidFilenameChars(filename) {
    if (!filename || typeof filename !== 'string') {
        return false;
    }
    return filename.includes('[') || filename.includes(']') || 
           filename.includes('/') || filename.includes('\\');
}
